import React, { useCallback, useMemo } from 'react'
import NotificationBuilderReach from '../../../elements/notification-builder-reach'
import NotificationBuilderPreview from '../../../elements/notification-builder-preview'
import { buildSubmittedNotificationPackages, usePreviewOptions } from '../../../helpers'
import { ApiVersion } from '../../../../../enums/api-version.enum'
import NotificationBuilderAside from '../../../elements/notification-builder-aside'
import { DomainDto } from '../../../../../dtos/domain'
import { INotificationBuilderState } from '../../../interfaces/notification-builder-state.interface'
import { NotificationDispatchActionPack } from '../../../types'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService, NotificationService } from '../../../../../services'
import { AppState } from '../../../../../stores/app'
import FlagList from '../../../../../structs/flags-list'
import { Journey } from '../../../../../features/journeys/types/journey-api-response'
import { NotificationBuilderSubmitType } from '../../../enums'
import { AccountDto } from '../../../../../dtos/account.dto'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'

type NotificationBuilderViewRightAsideProps = {
    loading: boolean
    level: PlatformLevel
    domain: DomainDto
    builder: INotificationBuilderState
    dispatchChanges: (action: NotificationDispatchActionPack) => void
    onDistributionChange: (state: INotificationBuilderState) => void
    hideEstimatedReach: boolean
    hidePreview: boolean
    hideDevicePreviewAction: boolean
    hideInlinePreviewAction: boolean
    journey?: Journey
    journeyNotificationType?: string
    validateSubmit: (
        builder: INotificationBuilderState,
        domain: DomainDto,
        flags: FlagList,
        isPreview?: boolean,
        isDraft?: boolean,
        appService?: AppService,
    ) => Promise<boolean>
} & (
    | {
          level: 'domain'
          org?: never
          orgDomains?: never
      }
    | {
          level: 'org'
          org: AccountDto
          orgDomains: DomainDto[]
      }
)

export const NotificationBuilderViewRightAside = (props: NotificationBuilderViewRightAsideProps) => {
    const appState = useService(AppState)
    const notifService = useService(NotificationService)

    const {
        loading,
        level,
        domain,
        org,
        orgDomains,
        builder,
        hideEstimatedReach,
        hidePreview,
        hideDevicePreviewAction,
        hideInlinePreviewAction,
        journey,
        journeyNotificationType,
        dispatchChanges,
        onDistributionChange,
        validateSubmit,
    } = props

    const flags = appState.flags
    const previewOptions = usePreviewOptions(builder.channels, { defaultValue: [] })

    const campaignInfo = useMemo(
        () => ({
            campaign_id: journey?.id ?? null,
            campaign_notification_type: journeyNotificationType,
        }),
        [journey, journeyNotificationType],
    )

    const onPreviewRequest = useCallback(
        async (requestType: NotificationBuilderSubmitType) => {
            const valid = await validateSubmit(builder, domain, flags, true)
            if (!valid) return

            const builds = buildSubmittedNotificationPackages(domain, builder, flags, requestType, campaignInfo)
            const previewBuild = builds[builder.selectedVariantIdx]

            await notifService.sendNotification(domain.id, previewBuild, {
                cancellationKey: `notif-send-preview`,
                version: ApiVersion.V4,
            })
        },
        [builder, domain, flags, campaignInfo, validateSubmit],
    )

    const selectedVariant = builder.variants[builder.selectedVariantIdx]
    const audience = selectedVariant.getAudience()

    const availableDomains = useMemo(() => {
        return level === 'org' ? [...orgDomains] : [domain]
    }, [level, org, orgDomains, domain])

    const reachAudienceDomains = useMemo(() => {
        if (level !== 'org') return availableDomains

        const selectedDomainIds = audience.getDomainIds()
        if (!selectedDomainIds || audience.getIsAll()) return availableDomains

        return selectedDomainIds.length ? availableDomains.filter((d) => selectedDomainIds.includes(d.id)) : []
    }, [level, audience, availableDomains])

    const isLoading = builder.loading || loading

    if (hideEstimatedReach && hidePreview) {
        return null
    }

    return (
        <NotificationBuilderAside>
            {!hideEstimatedReach && (
                <NotificationBuilderReach
                    domain={reachAudienceDomains}
                    account={org}
                    builder={builder}
                    setBuilder={dispatchChanges}
                    hideVariantReach={true}
                    hideDistributionManager={!!org}
                    onDistributionChange={onDistributionChange}
                />
            )}

            {!hidePreview && (
                <NotificationBuilderPreview
                    level="domain"
                    levelId={domain.id}
                    loading={isLoading}
                    domain={domain}
                    source={builder.variants[builder.selectedVariantIdx]}
                    options={previewOptions}
                    onPreviewRequest={onPreviewRequest}
                    hideDevicePreview={hideDevicePreviewAction}
                    hideInlinePreview={hideInlinePreviewAction}
                />
            )}
        </NotificationBuilderAside>
    )
}
